import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Follow, User } from '../../types/social.types';
import { socialService } from '../../services/api/socialService';
import { useAuth } from '../../contexts/AuthContext';
import { ProfileAvatar } from '../../shared/components/ProfileAvatar';
import { FollowButton } from '../Social/FollowButton';
import './FollowersList.css';

interface FollowersListProps {
  userId: string;
  type: 'followers' | 'following';
}

const PAGE_SIZE = 15;

const FollowersList: React.FC<FollowersListProps> = ({ userId, type }) => {
  const { user: currentUser } = useAuth();
  const [follows, setFollows] = useState<Follow[]>([]);
  const [followingIds, setFollowingIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);

    const fetchList =
      type === 'followers'
        ? socialService.getFollowers(userId)
        : socialService.getFollowing(userId);

    const fetchFollowingIds = currentUser
      ? socialService.getFollowing(currentUser._id)
      : Promise.resolve({ following: [], total: 0, page: 1 });

    Promise.all([fetchList, fetchFollowingIds])
      .then(([listData, followingData]) => {
        if (!cancelled) {
          const listFollows: Follow[] =
            type === 'followers'
              ? ((listData as any).followers ?? [])
              : ((listData as any).following ?? []);

          const myFollowing: Follow[] = (followingData as any).following ?? [];
          const ids = new Set(myFollowing.map((f: Follow) => f.followingId));

          setFollows(listFollows);
          setFollowingIds(ids);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load users');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userId, type, currentUser]);

  const handleFollowChange = useCallback(
    (targetUserId: string, isNowFollowing: boolean) => {
      setFollowingIds((prev) => {
        const next = new Set(prev);
        if (isNowFollowing) {
          next.add(targetUserId);
        } else {
          next.delete(targetUserId);
        }
        return next;
      });
    },
    []
  );

  const paged = follows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(follows.length / PAGE_SIZE);

  if (loading) {
    return (
      <div className='fl-loading'>
        <Spinner
          animation='border'
          size='sm'
        />
      </div>
    );
  }

  if (error) {
    return <p className='text-danger small'>{error}</p>;
  }

  if (follows.length === 0) {
    return (
      <p className='fl-empty text-muted'>
        {type === 'followers'
          ? 'No followers yet.'
          : 'Not following anyone yet.'}
      </p>
    );
  }

  return (
    <div className='fl'>
      {paged.map((follow) => {
        const person: User | undefined =
          type === 'followers' ? follow.follower : follow.following;
        const personId =
          type === 'followers' ? follow.followerId : follow.followingId;

        if (!person) return null;

        const isMutual = followingIds.has(personId);
        const isCurrentUser = currentUser?._id === personId;

        return (
          <div
            key={follow._id}
            className='fl-card'
          >
            <Link
              to={`/app/users/${personId}`}
              className='fl-card__link'
            >
              <ProfileAvatar
                src={person.profileImage}
                username={person.username}
                size='md'
              />
              <div className='fl-card__info'>
                <span className='fl-card__username'>{person.username}</span>
                {isMutual && (
                  <span
                    className='fl-card__mutual'
                    title='Mutual follow'
                  >
                    🤝 Mutual
                  </span>
                )}
              </div>
            </Link>
            {!isCurrentUser && (
              <div className='fl-card__action'>
                <FollowButton
                  userId={personId}
                  initialIsFollowing={followingIds.has(personId)}
                  onFollowChange={(isFollowing: boolean) =>
                    handleFollowChange(personId, isFollowing)
                  }
                  size='sm'
                />
              </div>
            )}
          </div>
        );
      })}

      {totalPages > 1 && (
        <div className='fl-pagination'>
          <button
            className='btn btn-outline-secondary btn-sm'
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className='fl-pagination__info'>
            {page} / {totalPages}
          </span>
          <button
            className='btn btn-outline-secondary btn-sm'
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default FollowersList;
